/**
 * ProjectSelector Component
 *
 * Custom dropdown selector for switching between projects in the Recursos Dashboard.
 * Displays project name with radio icon and custom dropdown button.
 *
 * Design specs from Figma (node 3547-7927):
 * - Border: 1px solid #5d5d5d
 * - Border-radius: 10px
 * - Width: 237px, Height: 50px
 * - Padding: 20px
 * - Gap: 20px between elements
 * - Radio icon: 18x18
 * - Project name: 16px, #F6F6F6
 * - Dropdown button: 40x40 circular with rotated arrow
 *
 * Props:
 * - projects: Array of available projects
 * - selectedProject: Currently selected project
 * - onProjectChange: Callback when selection changes
 */

import { useState, useRef, useEffect } from "react";
import { Circle, ChevronDown } from "lucide-react";
import { ProjectOption } from "@/types/recursos-dashboard.interface";

interface ProjectSelectorProps {
  /** Array of available projects for selection */
  projects: ProjectOption[];

  /** Currently selected project */
  selectedProject: ProjectOption;

  /** Callback fired when project selection changes */
  onProjectChange: (projectId: string) => void;
}

export function ProjectSelector({
  projects,
  selectedProject,
  onProjectChange,
}: ProjectSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectProject = (projectId: string) => {
    onProjectChange(projectId);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Main Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="border border-[#5d5d5d] rounded-lg flex items-center gap-3 px-3 py-2 h-9 w-56 bg-[#0C1427] hover:bg-[#1a2847] transition-colors"
      >
        {/* Radio Icon */}
        <Circle size={14} className="text-[#7846FF] flex-shrink-0" strokeWidth={2} />

        {/* Project Name */}
        <p className="text-sm text-[#F6F6F6] font-normal flex-1 text-left truncate">
          {selectedProject.name}
        </p>

        {/* Dropdown Arrow Button */}
        <div className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-[#15203C] transition-colors flex-shrink-0">
          <ChevronDown size={14} className="text-white transition-transform" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-[calc(100%+4px)] left-0 w-full bg-[#15203C] border border-[#3D4C6B] rounded-lg shadow-lg z-50 overflow-hidden">
          {projects.map((project) => (
            <button
              key={project.id}
              onClick={() => handleSelectProject(project.id)}
              className="w-full px-3 py-2 text-left hover:bg-[#1a2847] transition-colors flex flex-col gap-0.5 border-b border-[#3D4C6B] last:border-b-0"
            >
              {/* Project Name */}
              <span className="text-xs font-medium text-[#F6F6F6]">
                {project.name}
              </span>
              {/* SALIC Number */}
              <span className="text-[10px] text-[#8E98A8]">
                SALIC: {project.salicNumber}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
